import { Injectable, Scopes } from '@visiba/instantiation';
import { Signal, signal } from '@preact/signals-react';
import { NiN } from '@visiba/national-identity-number';
import { Logger } from '@visiba/logger';
import { todo } from '@cortex-shim/todo';
import { type TokenOverrides } from '@cellula/react';

@Injectable({
	scope: Scopes.Singleton,
})
export class UniversalConfig {
	public currentUnitId: Nullable<number> = null;
	public beWebVersion = '';
	public recentlyVisitedReceptionId = signal<number | null>(null);

	private loadedCriticalData = signal<CriticalData | null>(null);
	private readonly logger = new Logger('universal config');

	public get globalCustomerId() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.globalCustomerId;
	}
	public get logo() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return this.theme['EmailLogoUrl'] ?? null;
	}
	public get selectedUnitId() {
		const d = this.loadedCriticalData.peek();

		if (this.currentUnitId != null) {
			return this.currentUnitId;
		}

		if (d?.rootUnitId != null) {
			return d.rootUnitId;
		}

		todo('how to handle this case?');
	}
	public get licenceHolderId() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.licenceHolderId;
	}
	public get rootUnit() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.rootUnitId;
	}
	public get selectedCountryCode() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.selectedCountryCode;
	}
	public get selectedLanguageCode() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.selectedLanguageCode;
	}
	public get theme() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.theme;
	}
	public get alias() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.urlAlias;
	}
	public get availableLanguages() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.availableLanguages;
	}
	public get requireClientEmail() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.requireClientEmail;
	}
	public get requireClientPhone() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.requireClientPhone;
	}
	public get hideProvidedByVisibaCare() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.hideProvidedByVisibaCare;
	}
	public get daysToDepersonalizeClientAfterRequest() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.daysToDepersonalizeClientAfterRequest;
	}
	public get legal() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.legal;
	}
	public get appConfig() {
		const d = this.loadedCriticalData.peek();
		if (d == null) todo('how to handle this case?');

		return d.appConfig;
	}

	public insertCriticalData(criticalData: CriticalData) {
		const d = this.loadedCriticalData.peek();
		if (d != null) {
			this.logger.logWarning('cannot rebind critical data twice');

			return;
		}

		this.loadedCriticalData.value = criticalData;
		this.logger.logInformation('retrieved critical data');
	}
}

interface Opts {
	readonly availableLanguages: string[];
	readonly rootUnitId: number;
	readonly licenceHolderId: number;
	readonly globalCustomerId: string;
	readonly initialCountryCode: NiN.ImplementationsKeys;
	readonly languageCode: string;
	readonly urlAlias: string;
	readonly theme: TokenOverrides;
	readonly requireClientEmail: boolean;
	readonly requireClientPhone: boolean;
	readonly hideProvidedByVisibaCare: boolean;
	readonly daysToDepersonalizeClientAfterRequest: number;
	readonly legal: {
		readonly tos: string;
		readonly pp: string;
	};
	readonly appConfig: Nullable<AppConfig>;
}

export class CriticalData {
	public readonly availableLanguages: string[];
	public readonly urlAlias: string;
	public readonly globalCustomerId: string;
	public readonly rootUnitId: number;
	public readonly licenceHolderId: number;
	public readonly theme: TokenOverrides;
	public readonly requireClientEmail: boolean;
	public readonly requireClientPhone: boolean;
	public readonly hideProvidedByVisibaCare: boolean;
	public readonly daysToDepersonalizeClientAfterRequest: number;
	public readonly legal: {
		readonly tos: string;
		readonly pp: string;
	};
	public readonly appConfig: Nullable<AppConfig>;
	public selectedCountryCode: Signal<NiN.ImplementationsKeys>;
	public selectedLanguageCode: Signal<string>;

	constructor(opts: Opts) {
		this.availableLanguages = opts.availableLanguages;
		this.urlAlias = '/' + opts.urlAlias;
		this.rootUnitId = opts.rootUnitId;
		this.licenceHolderId = opts.licenceHolderId;
		this.globalCustomerId = opts.globalCustomerId;
		this.theme = opts.theme;
		this.requireClientEmail = opts.requireClientEmail;
		this.requireClientPhone = opts.requireClientPhone;
		this.hideProvidedByVisibaCare = opts.hideProvidedByVisibaCare;
		this.daysToDepersonalizeClientAfterRequest = opts.daysToDepersonalizeClientAfterRequest;
		this.legal = opts.legal;
		this.appConfig = opts.appConfig;

		this.selectedCountryCode = signal<NiN.ImplementationsKeys>(opts.initialCountryCode);
		this.selectedLanguageCode = signal(opts.languageCode);
	}
}

interface AppConfig {
	androidPackageId: string | null;
	appStoreId: string | null;
}
